// @ts-check

import * as Sentry from "@sentry/react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { ChatSessionQueryOptions, ChatSessionMutationOptions } from "./query";
import { ChatMessageQueryOptions } from "../messages/query";
import useSearchQuery from "@/hooks/useSearchQuery";
import { atom, useAtom, useAtomValue } from "jotai";
import { useSelector } from "react-redux";
import { activeProjectIdAtom } from "../projects";
import useSharedEffect from "@/hooks/useSharedEffect";
import { useActiveSessionHasActiveStream, useGetChatSessionStream } from "../streams";
import { ChatProviderError, ErrorCodes } from "../errorCodes";
import { pendingMessageAtom, pendingAgentMessageAtom } from "../messages";
import { pendingReportAtom } from "../reports";
import { pendingReviewAtom } from "../reviewsQuery";

const ACTIVE_CHAT_SESSION_ID_QUERY_KEY = "id"

const isAwaitingNewSessionAtom = atom(false)

function useActiveChatSessionId() {
  return useSearchQuery(ACTIVE_CHAT_SESSION_ID_QUERY_KEY)
}

function useIsAwaitingNewSession() {
  return useAtom(isAwaitingNewSessionAtom)
}

function useChatSessionList() {
  // @ts-ignore
  const { user } = useSelector((state) => state.app.datastate)
  const activeProjectId = useAtomValue(activeProjectIdAtom)

  return useInfiniteQuery(
    ChatSessionQueryOptions.CHAT_SESSION_LIST({ userToken: user?.userToken, projectId: activeProjectId })
  )
}

/**
 * @param {ChatSession} session
 * @param {string} firstMessageText
 * @param {Object} options
 * @param {AbortSignal=} options.signal
 */
async function getHistoryTitle(session, firstMessageText, { signal } = {}) {
  // TODO: Add a better implementation, maybe let the model generate the title
  // TODO: and probably handled on the backend
  if (session.patientInfo == null) return firstMessageText;
  return `${session.patientInfo.name} - ${firstMessageText}`;
}

const GetActiveStreamSymbol = Symbol('GetActiveStream')
const GenerateActiveChatSessionTitleSymbol = Symbol('GenerateActiveChatSessionTitle')

function useActiveChatSession() {
  // @ts-ignore
  const { user } = useSelector((state) => state.app.datastate)
  const activeProjectId = useAtomValue(activeProjectIdAtom)
  const [activeSessionId] = useActiveChatSessionId()

  const activeChatSessionQuery = useQuery(
    ChatSessionQueryOptions.CHAT_SESSION({
      sessionId: activeSessionId,
      userToken: user?.userToken,
      projectId: activeProjectId
    })
  )

  const { mutate: updateSession } = useUpdateChatSession()
  const isLoadingChatSession = activeChatSessionQuery.isLoading
  const activeChatSession = activeChatSessionQuery.data

  const messagesQuery = useInfiniteQuery(
    ChatMessageQueryOptions.CHAT_MESSAGES({ sessionId: activeSessionId, userToken: user?.userToken })
  )
  const chatMessages = messagesQuery.data ?? []

  const { mutateAsync: getSessionStream } = useGetChatSessionStream()
  const activeSessionHasActiveStream = useActiveSessionHasActiveStream()
  const hasPendingMessage = useAtomValue(pendingMessageAtom) != null
  const hasPendingAgentMessage = useAtomValue(pendingAgentMessageAtom) != null
  const hasPendingReport = useAtomValue(pendingReportAtom) != null
  const hasPendingReview = useAtomValue(pendingReviewAtom)

  useSharedEffect(GetActiveStreamSymbol, () => {
    if (activeSessionId == null) return
    if (activeChatSessionQuery.status !== "success") return
    if (activeSessionHasActiveStream) return
    if (hasPendingMessage || hasPendingAgentMessage || hasPendingReport || hasPendingReview) return

    const controller = new AbortController();

    getSessionStream({
      sessionId: activeSessionId,
      signal: controller.signal
    }).catch(() => {
      // Errors are already surfaced via useStreamErrorHandler's toast/reconnect
      // logic inside useGetChatSessionStream; this just prevents an unhandled
      // promise rejection when there's no active stream to reconnect to.
    })

    return () => controller.abort();
  }, [activeSessionId, activeChatSessionQuery.status])

  useSharedEffect(GenerateActiveChatSessionTitleSymbol, () => {
    if (isLoadingChatSession) return;
    if (activeChatSession == null) return;
    if (activeChatSession.title != null) return;
    if (chatMessages.length <= 0) return;
    const firstMessageText = chatMessages[0]?.text
    if (!firstMessageText) return;

    const controller = new AbortController();

    getHistoryTitle(activeChatSession, firstMessageText, { signal: controller.signal })
      .then((title) => {
        updateSession({
          sessionId: activeSessionId,
          sessionInfo: { title }
        });
      })
      .catch((err) => {
        Sentry.captureException(err, {
          extra: {
            context: 'getHistoryTitle - chat session title generation',
            activeSessionId,
            chatLength: chatMessages.length,
            aborted: controller.signal.aborted
          }
        });
        // Silent error
      });

    return () => {
      controller.abort();
    };
  }, [isLoadingChatSession, activeSessionId, chatMessages.length]);

  return activeChatSessionQuery
}

function useCreateChatSession() {
  const { user } = useSelector(state => state.app.datastate)
  const activeProjectId = useAtomValue(activeProjectIdAtom)
  const [_, setActiveChatSessionId] = useActiveChatSessionId()

  const mutationOptions = ChatSessionMutationOptions.CREATE_NEW_SESSION({
    projectId: activeProjectId,
    userToken: user?.userToken
  })

  return useMutation({
    ...mutationOptions,
    onSuccess: (data, variables, mutationResult, context) => {
      mutationOptions.onSuccess?.(data, variables, mutationResult, context)
      // @ts-ignore
      setActiveChatSessionId(data.session._id)
    }
  })
}

function useUpdateChatSession() {
  const { user } = useSelector(state => state.app.datastate)
  const activeProjectId = useAtomValue(activeProjectIdAtom)

  return useMutation(ChatSessionMutationOptions.UPDATE_SESSION({
    projectId: activeProjectId,
    userToken: user?.userToken
  }))
}

function useDeleteChatSession() {
  const { user } = useSelector(state => state.app.datastate)
  const activeProjectId = useAtomValue(activeProjectIdAtom)
  const [activeChatSessionId, setActiveChatSessionId] = useActiveChatSessionId()
  
  const mutationOptions = ChatSessionMutationOptions.DELETE_SESSION({
    projectId: activeProjectId,
    userToken: user?.userToken
  })

  return useMutation({
    ...mutationOptions,
    onSuccess: (data, variables, mutationResult, context) => {
      mutationOptions.onSuccess?.(data, variables, mutationResult, context)

      if(variables.sessionId !== activeChatSessionId) return
      setActiveChatSessionId(null)
    }
  })
}

export * from "./query"
export {
  ACTIVE_CHAT_SESSION_ID_QUERY_KEY,
  isAwaitingNewSessionAtom,

  useActiveChatSessionId,
  useIsAwaitingNewSession,
  useChatSessionList,
  useActiveChatSession,

  useCreateChatSession,
  useUpdateChatSession,
  useDeleteChatSession,
}
